/* iter-113 — TileFolderTree.
 * Extracted from TilePanelDialog.jsx because the deeply-recursive
 * `FolderNode` JSX caused babel-loader to hit Node's default call-stack
 * limit when traversing the AST. Splitting it out keeps each file's
 * traversal small enough to compile cleanly under CRA's webpack config.
 */
import { useState } from 'react';
import {
  Download, ExternalLink, Folder, FolderPlus,
  ChevronRight, ChevronDown, Plus, Trash2,
} from 'lucide-react';
import FilePreviewThumb from './FilePreviewThumb';

const DRIVE_PROVIDERS = {
  'google-drive': { label: 'Google Drive', color: '#1A73E8', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg' },
  'sharepoint':   { label: 'SharePoint',   color: '#0078D4', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e1/Microsoft_Office_SharePoint_%282019%E2%80%93present%29.svg' },
  'onedrive':     { label: 'OneDrive',     color: '#0078D4', logo: 'https://upload.wikimedia.org/wikipedia/commons/3/3c/Microsoft_Office_OneDrive_%282019%E2%80%93present%29.svg' },
  'dropbox':      { label: 'Dropbox',      color: '#0061FF', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/78/Dropbox_Icon.svg' },
  'box':          { label: 'Box',          color: '#0061D5', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/57/Box%2C_Inc._logo.svg' },
};

function deriveLinkKind(ln) {
  if (ln.kind && DRIVE_PROVIDERS[ln.kind]) return ln.kind;
  const u = (ln.url || '').toLowerCase();
  if (u.includes('drive.google.com') || u.includes('docs.google.com') || u.includes('sheets.google.com')) return 'google-drive';
  if (u.includes('sharepoint.com')) return 'sharepoint';
  if (u.includes('onedrive.live.com') || u.includes('1drv.ms') || u.includes('onedrive.com')) return 'onedrive';
  if (u.includes('dropbox.com') || u.includes('db.tt')) return 'dropbox';
  if (u.includes('box.com')) return 'box';
  return 'other';
}

function fmtSize(bytes) {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function absoluteUrl(u) {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export function LinkRow({ ln, color, canEdit, onDelete }) {
  const kind = deriveLinkKind(ln);
  const provider = DRIVE_PROVIDERS[kind];
  const style = provider
    ? { borderColor: `${provider.color}33`, background: 'white', borderLeftWidth: 4, borderLeftColor: provider.color }
    : { borderColor: '#EAE5D9', background: 'white' };
  return (
    <div className="group flex items-center gap-2">
      <a
        href={ln.url}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={`tile-panel-link-${ln.id}`}
        className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg border hover:shadow-sm transition-shadow"
        style={style}
      >
        {provider
          ? <img src={provider.logo} alt={provider.label} className="w-6 h-6 flex-shrink-0" loading="lazy" />
          : <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color }} />}
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope', fontWeight: 600 }}>{ln.label}</p>
          <p className="text-[10px] truncate" style={{ color: provider ? provider.color : '#7A8C8C', fontFamily: 'Manrope' }}>
            {provider ? provider.label : ln.url}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 flex-shrink-0 opacity-60" style={{ color: '#4A5D5D' }} />
      </a>
      {canEdit && (
        <button
          type="button"
          onClick={() => onDelete(ln)}
          className="p-1.5 rounded text-red-600 hover:bg-red-50"
          aria-label="Delete link"
          data-testid={`tile-panel-link-delete-${ln.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export function FileRow({ f, color, canEdit, onDelete }) {
  return (
    <div className="group flex items-center gap-2">
      <a
        href={absoluteUrl(f.url)}
        download={f.filename}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={`tile-panel-file-${f.id}`}
        className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg border hover:shadow-sm transition-shadow"
        style={{ borderColor: '#EAE5D9', background: 'white' }}
      >
        <FilePreviewThumb url={f.url} filename={f.filename} size="md" color={color} />
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope', fontWeight: 600 }}>{f.filename}</p>
          {f.size_bytes > 0 && (
            <p className="text-[10px]" style={{ color: '#7A8C8C' }}>{fmtSize(f.size_bytes)}</p>
          )}
        </div>
        <Download className="w-4 h-4 flex-shrink-0 opacity-60" style={{ color: '#4A5D5D' }} />
      </a>
      {canEdit && (
        <button
          type="button"
          onClick={() => onDelete(f)}
          className="p-1.5 rounded text-red-600 hover:bg-red-50"
          aria-label="Delete file"
          data-testid={`tile-panel-file-delete-${f.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export function AddFolderForm({ color, parentId, onCreate, onCancel }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const v = (name || '').trim();
    if (!v) return;
    setBusy(true);
    try { await onCreate({ name: v, parent_id: parentId || null }); setName(''); onCancel?.(); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-dashed p-2 flex gap-1.5 items-center" style={{ borderColor: `${color}55`, background: '#fff' }}>
      <FolderPlus className="w-4 h-4" style={{ color }} />
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel?.(); }}
        placeholder="Folder name"
        className="flex-1 text-[12px] rounded border px-2 py-1"
        style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
        data-testid="tile-panel-folder-name-input"
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        className="text-[11px] font-bold py-1 px-2 rounded text-white disabled:opacity-50"
        style={{ background: color, fontFamily: 'Manrope' }}
        data-testid="tile-panel-folder-submit-btn"
      >
        {busy ? '…' : 'Add'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-[11px] font-bold py-1 px-2 rounded"
        style={{ background: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
      >
        Cancel
      </button>
    </div>
  );
}

export function AddLinkForm({ color, folderId, onCreate, onCancel }) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const u = (url || '').trim();
    if (!u) return;
    setBusy(true);
    try { await onCreate({ label: (label || u).trim(), url: u, folder_id: folderId || null }); setLabel(''); setUrl(''); onCancel?.(); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-dashed p-2.5 space-y-1.5" style={{ borderColor: `${color}55`, background: '#fff' }}>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (optional)"
        className="w-full text-[12px] rounded border px-2 py-1.5"
        style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
        data-testid="tile-panel-link-label-input"
      />
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://drive.google.com/…"
        className="w-full text-[12px] rounded border px-2 py-1.5"
        style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
        data-testid="tile-panel-link-url-input"
      />
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="flex-1 text-[11px] font-bold py-1.5 rounded text-white disabled:opacity-50"
          style={{ background: color, fontFamily: 'Manrope' }}
          data-testid="tile-panel-link-submit-btn"
        >
          {busy ? 'Adding…' : 'Add link'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] font-bold py-1.5 px-3 rounded"
          style={{ background: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function FolderNodeHeader({ folder, depth, color, canEdit, count, onToggle, isOpen, isEmpty, actions, openAddSub, openAddLink }) {
  return (
    <div
      className="flex items-center gap-1.5 py-1.5 px-2 rounded-md group"
      style={{ background: depth === 0 ? `${color}10` : 'transparent', paddingLeft: depth * 16 + 8 }}
    >
      <button type="button" onClick={onToggle} className="text-gray-500 hover:text-gray-800">
        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      <Folder className="w-4 h-4 flex-shrink-0" style={{ color }} />
      <span className="text-[13px] font-bold flex-1 truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
        {folder.name}
      </span>
      <span className="text-[10px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
        {count || '—'}
      </span>
      {canEdit && (
        <div className="md:opacity-40 md:group-hover:opacity-100 opacity-100 transition-opacity flex items-center gap-0.5">
          <button
            type="button"
            onClick={openAddSub}
            className="p-1 rounded hover:bg-white text-gray-600"
            title="Add sub-folder"
            data-testid={`tile-panel-folder-add-sub-${folder.id}`}
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={openAddLink}
            className="p-1 rounded hover:bg-white text-gray-600"
            title="Add link in this folder"
            data-testid={`tile-panel-folder-add-link-${folder.id}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => {
              const msg = isEmpty
                ? `Delete folder "${folder.name}"?`
                : `Folder "${folder.name}" is not empty. Delete it AND everything inside?`;
              if (window.confirm(msg)) actions.deleteFolder(folder, !isEmpty);
            }}
            className="p-1 rounded hover:bg-red-50 text-red-600"
            title="Delete folder"
            data-testid={`tile-panel-folder-delete-${folder.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * FolderTree — iterative renderer.
 *
 * Flattens the folder tree into a single array of rows so we can avoid
 * a recursive React component (babel-loader's AST traversal of a
 * self-referencing component blew Node's call stack in dev mode).
 *
 * Each row is one folder header + (when open) its direct files/links +
 * inline add forms. Children appear as subsequent rows with increasing
 * depth. Collapsed folders skip their descendant rows entirely.
 */
export function FolderTree({ color, canEdit, rootKids, childrenByParent, filesByFolder, linksByFolder, actions }) {
  // Per-folder UI state (collapsed, add-sub-folder open, add-link open)
  const [uiState, setUiState] = useState({}); // { [folderId]: { open, addingFolder, addingLink } }

  const getState = (id) => uiState[id] || { open: true, addingFolder: false, addingLink: false };
  const patch = (id, updates) =>
    setUiState((s) => ({ ...s, [id]: { ...getState(id), ...updates } }));

  // Iterative DFS to produce a flat list of rows.
  const rows = [];
  const stack = (rootKids || []).slice().reverse().map((f) => ({ folder: f, depth: 0 }));
  while (stack.length) {
    const { folder, depth } = stack.pop();
    const state = getState(folder.id);
    const kids = childrenByParent[folder.id] || [];
    const files = filesByFolder[folder.id] || [];
    const links = linksByFolder[folder.id] || [];
    rows.push({ folder, depth, state, kids, files, links });
    if (state.open) {
      // Push kids in reverse so they pop in original order
      for (let i = kids.length - 1; i >= 0; i--) {
        stack.push({ folder: kids[i], depth: depth + 1 });
      }
    }
  }

  if (!rows.length) return null;
  return (
    <div data-testid="tile-panel-folder-tree" className="space-y-1">
      {rows.map(({ folder, depth, state, kids, files, links }) => {
        const isEmpty = !kids.length && !files.length && !links.length;
        const count = kids.length + files.length + links.length;
        const padLeft = depth * 16 + 24;
        return (
          <div key={folder.id} data-testid={`tile-panel-folder-${folder.id}`}>
            <FolderNodeHeader
              folder={folder}
              depth={depth}
              color={color}
              canEdit={canEdit}
              count={count}
              onToggle={() => patch(folder.id, { open: !state.open })}
              isOpen={state.open}
              isEmpty={isEmpty}
              actions={actions}
              openAddSub={() => patch(folder.id, { addingFolder: true })}
              openAddLink={() => patch(folder.id, { addingLink: true })}
            />
            {state.open && (
              <div className="space-y-1.5 mt-1 mb-2" style={{ paddingLeft: padLeft }}>
                {canEdit && state.addingFolder && (
                  <AddFolderForm
                    color={color}
                    parentId={folder.id}
                    onCreate={actions.createFolder}
                    onCancel={() => patch(folder.id, { addingFolder: false })}
                  />
                )}
                {canEdit && state.addingLink && (
                  <AddLinkForm
                    color={color}
                    folderId={folder.id}
                    onCreate={actions.createLink}
                    onCancel={() => patch(folder.id, { addingLink: false })}
                  />
                )}
                {links.map((ln) => (
                  <LinkRow key={ln.id} ln={ln} color={color} canEdit={canEdit} onDelete={actions.deleteLink} />
                ))}
                {files.map((f) => (
                  <FileRow key={f.id} f={f} color={color} canEdit={canEdit} onDelete={actions.deleteFile} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
